import { useState, type FocusEvent, type MouseEvent } from 'react';
import { ExclamationCircleIcon, LockClosedIcon, TrashIcon } from '@heroicons/react/24/outline';
import { SolidStatusPill } from '@odo/shared-ui';
import { TeamPill } from '@odo/shared-ui/components/TeamPill';
import { VirtualAppointmentDueBadge } from './VirtualAppointmentDueBadge';
import {
    VIRTUAL_APPOINTMENT_STATUS_LABELS,
    formatSummaryTrigger,
    fullTypeTitle,
    isFrozenSummary,
    type VirtualAppointmentSummary
} from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { VirtualAppointmentTypeTitle } from '@odo/shared-ui/clinical/virtualAppointments/VirtualAppointmentTypeTitle';

interface VirtualAppointmentListItemProps {
    summary: VirtualAppointmentSummary;
    selected: boolean;
    hasErrors: boolean;
    due?: boolean;
    onSelect: (key: string) => void;
    /** Omit for rows that cannot be removed (frozen VAs, read-only surfaces). */
    onRemove?: (key: string) => void;
}

function rowLabel(summary: VirtualAppointmentSummary, due: boolean): string {
    const base = `${fullTypeTitle(summary)}, ${formatSummaryTrigger(summary)}, ${summary.team}`;
    if (isFrozenSummary(summary)) {
        return `${base}, ${VIRTUAL_APPOINTMENT_STATUS_LABELS[summary.status]}`;
    }
    return due ? `${base}, Due` : base;
}

interface RowTitleProps {
    summary: VirtualAppointmentSummary;
    selected: boolean;
    hasErrors: boolean;
    due: boolean;
    onSelect: () => void;
}

// The title is the row's focusable select control; its overlay stretches over the whole row so
// the pills (divs) sit beside it without being nested in a button.
function RowTitle({ summary, selected, hasErrors, due, onSelect }: RowTitleProps) {
    const weight = selected ? 'font-semibold text-gray-900' : 'font-medium text-gray-800';
    return (
        <span className="flex items-center gap-1.5 min-w-0">
            <button
                type="button"
                onClick={(event) => {
                    event.stopPropagation();
                    onSelect();
                }}
                aria-current={selected ? 'true' : undefined}
                aria-label={rowLabel(summary, due)}
                className="flex min-w-0 text-left text-sm focus:outline-none after:absolute after:inset-0 after:rounded-lg focus-visible:after:ring-2 focus-visible:after:ring-gray-400"
            >
                <VirtualAppointmentTypeTitle summary={summary} codeClassName={weight} />
            </button>
            {isFrozenSummary(summary) && (
                <LockClosedIcon
                    className="w-3.5 h-3.5 flex-shrink-0 text-gray-400"
                    aria-label="Read-only"
                />
            )}
            {hasErrors && (
                <ExclamationCircleIcon
                    className="w-4 h-4 flex-shrink-0 text-red-500"
                    aria-label="Incomplete"
                />
            )}
        </span>
    );
}

interface RemoveButtonProps {
    visible: boolean;
    onRemove: (event: MouseEvent<HTMLButtonElement>) => void;
}

function RemoveButton({ visible, onRemove }: RemoveButtonProps) {
    const visibility = visible
        ? 'opacity-100'
        : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto';
    return (
        <button
            type="button"
            onClick={onRemove}
            aria-label="Remove VA"
            title="Remove"
            tabIndex={visible ? 0 : -1}
            className={`flex items-center justify-center w-6 h-6 rounded-md text-gray-400 hover:bg-gray-100 hover:text-red-500 transition-opacity ${visibility}`}
        >
            <TrashIcon className="w-4 h-4" />
        </button>
    );
}

interface SecondarySlotProps {
    summary: VirtualAppointmentSummary;
    due: boolean;
    removeVisible: boolean;
    onRemove?: (event: MouseEvent<HTMLButtonElement>) => void;
}

function SecondaryContent({ summary, due, removeVisible, onRemove }: SecondarySlotProps) {
    if (onRemove) return <RemoveButton visible={removeVisible} onRemove={onRemove} />;
    if (isFrozenSummary(summary)) {
        return (
            <SolidStatusPill
                value={summary.status}
                label={VIRTUAL_APPOINTMENT_STATUS_LABELS[summary.status]}
                compact
            />
        );
    }
    return due ? <VirtualAppointmentDueBadge /> : null;
}

// Fixed-height slot: remove for draft rows, the status pill for frozen ones, "Due" for due planned.
function SecondarySlot(props: SecondarySlotProps) {
    return (
        <span className="relative z-10 justify-self-end flex items-center justify-end h-6 min-w-[1.5rem]">
            <SecondaryContent {...props} />
        </span>
    );
}

// Line 1: title, team. Line 2: trigger, then remove (planned) or status (frozen).
export function VirtualAppointmentListItem({
    summary,
    selected,
    hasErrors,
    due = false,
    onSelect,
    onRemove
}: VirtualAppointmentListItemProps) {
    const [focusWithin, setFocusWithin] = useState(false);
    const select = () => onSelect(summary.key);
    const handleRemove = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onRemove?.(summary.key);
    };
    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setFocusWithin(false);
        }
    };
    const stateClasses = selected ? 'bg-gray-200' : 'hover:bg-gray-100';

    return (
        <div
            onClick={select}
            onFocus={() => setFocusWithin(true)}
            onBlur={handleBlur}
            className={`group relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 px-2 py-2 rounded-lg cursor-pointer transition-colors duration-200 ${stateClasses}`}
        >
            <RowTitle
                summary={summary}
                selected={selected}
                hasErrors={hasErrors}
                due={due}
                onSelect={select}
            />
            <span className="relative z-10 justify-self-end">
                <TeamPill team={summary.team} size="xs" />
            </span>
            <span className="min-w-0 truncate text-xs text-gray-500" aria-hidden="true">
                {formatSummaryTrigger(summary)}
            </span>
            <SecondarySlot
                summary={summary}
                due={due}
                removeVisible={selected || focusWithin}
                onRemove={onRemove ? handleRemove : undefined}
            />
        </div>
    );
}
